import axios from 'axios';
import { TIENDANUBE_CONFIG, getTiendaNubeHeaders, getTiendaNubeApiUrl, getProductPublicUrl } from '@/config/tiendanube';
import tiendaNubeData from '@/data/tiendanube-products.json';

class TiendaNubeService {
    constructor() {
        this.storeId = TIENDANUBE_CONFIG.STORE_ID;
        this.accessToken = TIENDANUBE_CONFIG.ACCESS_TOKEN;
        this.baseURL = getTiendaNubeApiUrl();
        
        // Cache en memoria
        this.cache = {
            talleres: null,        // Datos en cache
            timestamp: null,       // Timestamp de cuando se guardó
            loading: false,        // Indica si hay una petición en curso
            error: null           // Último error si lo hubo
        };
    }

    // Verificar si el cache es válido
    isCacheValid() {
        if (!TIENDANUBE_CONFIG.CACHE.ENABLED) {
            return false;
        }

        if (!this.cache.talleres || !this.cache.timestamp) {
            return false;
        }

        const now = Date.now();
        const age = now - this.cache.timestamp;
        const isValid = age < TIENDANUBE_CONFIG.CACHE.TTL;

        if (!isValid) {
            console.log(`⏰ Cache expirado (edad: ${Math.round(age / 1000)}s, TTL: ${Math.round(TIENDANUBE_CONFIG.CACHE.TTL / 1000)}s)`);
        }

        return isValid;
    }

    // Guardar en cache
    saveToCache(talleres) {
        this.cache.talleres = talleres;
        this.cache.timestamp = Date.now();
        this.cache.error = null;
        
        console.log(`💾 Cache actualizado (${talleres.length} talleres, TTL: ${Math.round(TIENDANUBE_CONFIG.CACHE.TTL / 1000)}s)`);
    }

    // Limpiar cache manualmente
    clearCache() {
        this.cache.talleres = null;
        this.cache.timestamp = null;
        this.cache.error = null;
        console.log('🗑️ Cache limpiado');
    }

    // Obtener información del cache
    getCacheInfo() {
        if (!this.cache.timestamp) {
            return { status: 'empty', age: 0, ttl: TIENDANUBE_CONFIG.CACHE.TTL };
        }

        const age = Date.now() - this.cache.timestamp;
        const remaining = Math.max(0, TIENDANUBE_CONFIG.CACHE.TTL - age);
        
        return {
            status: this.isCacheValid() ? 'valid' : 'expired',
            age: Math.round(age / 1000), // en segundos
            remaining: Math.round(remaining / 1000), // en segundos
            ttl: Math.round(TIENDANUBE_CONFIG.CACHE.TTL / 1000), // en segundos
            itemsCount: this.cache.talleres?.length || 0,
            lastUpdate: new Date(this.cache.timestamp).toLocaleString('es-ES')
        };
    }

    // Headers por defecto para las peticiones
    getHeaders() {
        return getTiendaNubeHeaders();
    }

    // Obtener todos los productos
    async getAllProducts() {
        try {
            console.log('Obteniendo productos desde TiendaNube...');

            const response = await axios.get(getTiendaNubeApiUrl('products'), {
                headers: this.getHeaders(),
                params: {
                    per_page: TIENDANUBE_CONFIG.PRODUCTS_PER_PAGE,
                    page: 1
                }
            });

            console.log('Productos obtenidos desde TiendaNube:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error al obtener productos de TiendaNube:', error);
            throw error;
        }
    }

    // Obtener un producto específico por ID
    async getProductById(productId) {
        try {
            const response = await axios.get(getTiendaNubeApiUrl(`products/${productId}`), {
                headers: this.getHeaders()
            });

            return response.data;
        } catch (error) {
            console.error(`Error al obtener producto ${productId} de TiendaNube:`, error);
            throw error;
        }
    }

    // Obtener productos con filtros específicos
    async getProductsByCategory(categoryId) {
        try {
            const response = await axios.get(getTiendaNubeApiUrl('products'), {
                headers: this.getHeaders(),
                params: {
                    category_id: categoryId,
                    per_page: TIENDANUBE_CONFIG.PRODUCTS_PER_PAGE
                }
            });

            return response.data;
        } catch (error) {
            console.error(`Error al obtener productos de categoría ${categoryId}:`, error);
            throw error;
        }
    }

    // Obtener productos activos (publicados)
    async getActiveProducts() {
        try {
            const response = await axios.get(getTiendaNubeApiUrl('products'), {
                headers: this.getHeaders(),
                params: {
                    published: true,
                    per_page: TIENDANUBE_CONFIG.PRODUCTS_PER_PAGE
                }
            });

            return response.data;
        } catch (error) {
            console.error('Error al obtener productos activos de TiendaNube:', error);
            throw error;
        }
    }

    // Transformar productos de TiendaNube al formato esperado por la aplicación
    transformProductsToTalleres(products) {
        // Tags que indican que un producto debe estar oculto
        const tagsOcultos = ['oculto', 'hidden', 'no-mostrar', 'no-mostrar-en-web', 'ocultar'];
        
        return products
            .filter(product => {
                // Filtrar productos que tengan tags de oculto
                const tags = (product.tags || '').toString().toLowerCase();
                const tieneTagOculto = tagsOcultos.some(tag => tags.includes(tag.toLowerCase()));
                
                if (tieneTagOculto) {
                    console.log(`🚫 Producto oculto por tag: ${product.name?.es || product.name} (ID: ${product.id}, Tags: ${product.tags})`);
                }
                
                return !tieneTagOculto;
            })
            .map(product => {
                const variant = product.variants?.[0];
                const precio = variant?.price ? parseFloat(variant.price) : 0;
                const precioDescuento = variant?.promotional_price ? parseFloat(variant.promotional_price) : null;
                const precioOriginal = variant?.compare_at_price ? parseFloat(variant.compare_at_price) : precio;

                // Determinar si hay descuento y calcular porcentaje
                const tieneDescuento = precioDescuento && precioDescuento < precio;
                const precioFinal = tieneDescuento ? precioDescuento : precio;
                const precioComparacion = tieneDescuento ? precio : precioOriginal;
                const porcentajeDescuento = tieneDescuento ? Math.round(((precio - precioDescuento) / precio) * 100) : 0;

                // Determinar si está agotado basándose en el stock
                const stock = variant?.stock ?? null;
                const estaAgotado = stock !== null && stock <= 0;
                const boton = estaAgotado ? 'AGOTADO' : 'Reservar';

                return {
                    id: product.id,
                    slug: product.handle?.es || product.handle,
                    docente: this.extractDocente(product.name?.es || product.name, product.description?.es || product.description),
                    lema: product.name?.es || product.name,
                    inicio: this.extractFecha(product.description?.es || product.description),
                    precio: this.formatPrice(precioFinal), // Precio final (promocional si existe)
                    precioOriginal: tieneDescuento ? this.formatPrice(precioComparacion) : null,
                    precioDescuento: tieneDescuento ? this.formatPrice(precioDescuento) : null,
                    porcentajeDescuento: porcentajeDescuento > 0 ? porcentajeDescuento : null,
                    tieneDescuento: tieneDescuento,
                    precioUSD: null,
                    linkTienda: getProductPublicUrl(product.handle?.es || product.handle),
                    urlAfiche: product.images?.[0]?.src || product.image?.src,
                    boton: boton,
                    botonInfo: null,
                    colorBoton: '#2563eb',
                    // Campos adicionales de TiendaNube
                    description: product.description?.es || product.description,
                    published: product.published,
                    created_at: product.created_at,
                    updated_at: product.updated_at,
                    variants: product.variants,
                    // Información adicional útil
                    stock: variant?.stock || null,
                    sku: variant?.sku || null,
                    tags: product.tags || '',
                    // Fecha para ordenamiento
                    fechaOrdenamiento: new Date(product.created_at)
                };
            });
    }

    // Función auxiliar para extraer docente del título o descripción
    extractDocente(titulo, descripcion) {
        const patrones = [
            /con\s+([A-ZÁÉÍÓÚÑ][a-záéíóúñ\s]+)/i,
            /por\s+([A-ZÁÉÍÓÚÑ][a-záéíóúñ\s]+)/i,
            /dictado\s+por\s+([A-ZÁÉÍÓÚÑ][a-záéíóúñ\s]+)/i,
            /impartido\s+por\s+([A-ZÁÉÍÓÚÑ][a-záéíóúñ\s]+)/i
        ];

        for (const patron of patrones) {
            const match = titulo?.match(patron) || descripcion?.match(patron);
            if (match) {
                return match[1].trim().toUpperCase();
            }
        }

        return "DOCENTE ORSAI";
    }

    // Función auxiliar para extraer fecha de la descripción
    extractFecha(descripcion) {
        if (!descripcion) return "Próximamente";

        const patrones = [
            /(\d{1,2}\/\d{1,2}\/\d{4})/,
            /(\d{1,2}\s+de\s+[a-z]+)/i,
            /(desde\s+el\s+\d{1,2}\/\d{1,2})/i,
            /(inicia\s+el\s+\d{1,2}\/\d{1,2})/i,
            /(comienza\s+el\s+\w+\s+\d{1,2})/i
        ];

        for (const patron of patrones) {
            const match = descripcion.match(patron);
            if (match) {
                return match[1];
            }
        }

        return "Próximamente";
    }

    // Función auxiliar para formatear precio
    formatPrice(price) {
        if (!price) return "Consultar";

        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price).replace('ARS', '').trim();
    }

    // Función para separar campamentos y talleres virtuales
    separarCampamentosYTalleres(talleres) {
        const campamentos = [];
        const talleresVirtuales = [];

        talleres.forEach(taller => {
            const titulo = taller.lema?.toLowerCase() || '';
            const descripcion = taller.description?.toLowerCase() || '';

            // Detectar si es campamento
            const esCampamento = titulo.includes('campamento') ||
                descripcion.includes('campamento') ||
                taller.categoria?.toLowerCase().includes('campamento');

            if (esCampamento) {
                // Agregar información específica para campamentos
                campamentos.push({
                    ...taller,
                    tipo: 'campamento',
                    esCampamento: true
                });
            } else {
                // Agregar información específica para talleres virtuales
                talleresVirtuales.push({
                    ...taller,
                    tipo: 'taller-virtual',
                    esCampamento: false
                });
            }
        });

        return { campamentos, talleresVirtuales };
    }

    // Función para parsear fechas de inicio y ordenar inteligentemente
    parsearFechaInicio(fechaTexto) {
        if (!fechaTexto) return new Date('2099-12-31'); // Fechas sin texto van al final

        const fechaTextoLower = fechaTexto.toLowerCase();
        const mesActual = new Date().getMonth() + 1; // 1-12
        const añoActual = new Date().getFullYear();

        // Mapeo de meses en español
        const meses = {
            'enero': 1, 'febrero': 2, 'marzo': 3, 'abril': 4, 'mayo': 5, 'junio': 6,
            'julio': 7, 'agosto': 8, 'septiembre': 9, 'octubre': 10, 'noviembre': 11, 'diciembre': 12,
            'ene': 1, 'feb': 2, 'mar': 3, 'abr': 4, 'may': 5, 'jun': 6,
            'jul': 7, 'ago': 8, 'sep': 9, 'oct': 10, 'nov': 11, 'dic': 12
        };

        // Buscar mes en el texto
        let mesEncontrado = null;
        let añoEncontrado = añoActual;

        for (const [nombreMes, numeroMes] of Object.entries(meses)) {
            if (fechaTextoLower.includes(nombreMes)) {
                mesEncontrado = numeroMes;
                break;
            }
        }

        // Buscar año en el texto (4 dígitos)
        const añoMatch = fechaTexto.match(/\b(20\d{2})\b/);
        if (añoMatch) {
            añoEncontrado = parseInt(añoMatch[1]);
        } else {
            // Si no hay año específico, determinar si es este año o el próximo
            if (mesEncontrado && mesEncontrado < mesActual) {
                añoEncontrado = añoActual + 1;
            }
        }

        // Buscar día en el texto
        const diaMatch = fechaTexto.match(/\b(\d{1,2})\b/);
        const dia = diaMatch ? parseInt(diaMatch[1]) : 1;

        if (mesEncontrado) {
            return new Date(añoEncontrado, mesEncontrado - 1, dia);
        }

        return new Date('2099-12-31'); // Fechas no parseables van al final
    }

    // Función para ordenar talleres por fecha de inicio
    ordenarPorFechaInicio(talleres) {
        return talleres.sort((a, b) => {
            const fechaA = this.parsearFechaInicio(a.inicio);
            const fechaB = this.parsearFechaInicio(b.inicio);
            return fechaA - fechaB; // Del más cercano al más lejano
        });
    }

    // Obtener talleres desde TiendaNube (método principal con cache)
    async getTalleres(forceRefresh = false) {
        try {
            // 1. Si se fuerza refresh, limpiar cache
            if (forceRefresh) {
                console.log('🔄 Refresh forzado, limpiando cache...');
                this.clearCache();
            }

            // 2. Verificar si hay cache válido
            if (this.isCacheValid()) {
                const cacheInfo = this.getCacheInfo();
                console.log(`✅ Usando cache (${cacheInfo.itemsCount} talleres, expira en ${cacheInfo.remaining}s)`);
                return this.cache.talleres;
            }

            // 3. Evitar múltiples peticiones simultáneas
            if (this.cache.loading) {
                console.log('⏳ Petición en curso, esperando...');
                // Esperar un poco y retornar cache si está disponible
                await new Promise(resolve => setTimeout(resolve, 100));
                if (this.cache.talleres) {
                    return this.cache.talleres;
                }
            }

            // 4. Marcar como loading
            this.cache.loading = true;

            // 5. En producción usar endpoint propio (Netlify Function) para no exponer token ni CORS
            // En desarrollo usar el proxy de Vite que ya apunta a TiendaNube
            let talleres;

            try {
                if (!import.meta.env.DEV && TIENDANUBE_CONFIG.TALLERES_API_URL) {
                    // Producción: llamar a nuestra API (serverless) que consulta TiendaNube en el servidor
                    console.log('🌐 Consultando talleres vía API propia (producción)...');
                    const res = await fetch(TIENDANUBE_CONFIG.TALLERES_API_URL);
                    if (res.ok) {
                        talleres = await res.json();
                        console.log(`✅ Talleres obtenidos dinámicamente (${talleres.length} talleres)`);
                    } else {
                        throw new Error(`API talleres: ${res.status}`);
                    }
                } else {
                    // Desarrollo: consultar TiendaNube directamente (proxy añade el token)
                    console.log('🌐 Consultando API de TiendaNube...');
                    const products = await this.getActiveProducts();
                    talleres = this.transformProductsToTalleres(products);
                    const { campamentos, talleresVirtuales } = this.separarCampamentosYTalleres(talleres);
                    const campamentosOrdenados = this.ordenarPorFechaInicio(campamentos);
                    const talleresVirtualesOrdenados = this.ordenarPorFechaInicio(talleresVirtuales);
                    talleres = [...talleresVirtualesOrdenados, ...campamentosOrdenados];
                    console.log(`✅ API consultada exitosamente (${talleres.length} talleres)`);
                }
            } catch (apiError) {
                console.warn('⚠️ Error al consultar API, usando fallback a datos locales:', apiError.message);
                console.warn('⚠️ Detalles del error:', apiError.response?.data || apiError.message);
                talleres = this.getTalleresFromLocalData();
            }

            // 6. Guardar en cache
            this.saveToCache(talleres);

            // 7. Marcar como no loading
            this.cache.loading = false;

            return talleres;

        } catch (error) {
            console.error('❌ Error en getTalleres:', error);
            this.cache.loading = false;
            this.cache.error = error;

            // Fallback final: intentar usar cache antiguo o datos locales
            if (this.cache.talleres) {
                console.log('⚠️ Usando cache antiguo como fallback');
                return this.cache.talleres;
            }

            console.log('⚠️ Usando datos locales como último fallback');
            return this.getTalleresFromLocalData();
        }
    }

    // Método para obtener talleres desde datos locales
    getTalleresFromLocalData() {
        try {
            console.log('📦 Obteniendo talleres desde datos locales...');
            
            let talleres = [];

            // Si existen talleres ya transformados, usarlos
            if (tiendaNubeData && tiendaNubeData.talleres && Array.isArray(tiendaNubeData.talleres)) {
                console.log('✅ Usando talleres ya transformados del JSON');
                talleres = tiendaNubeData.talleres;
            }
            // Si no, transformar los productos crudos
            else if (tiendaNubeData && tiendaNubeData.productos && Array.isArray(tiendaNubeData.productos)) {
                console.log('🔄 Transformando productos crudos del JSON...');
                // Transformar productos usando el mismo método que la API
                // Esto asegura que se filtren productos con tags ocultos
                talleres = this.transformProductsToTalleres(tiendaNubeData.productos);
            } else {
                console.warn('⚠️ No se encontraron datos locales de TiendaNube (ni talleres ni productos)');
                return [];
            }

            // Separar campamentos y talleres virtuales
            const { campamentos, talleresVirtuales } = this.separarCampamentosYTalleres(talleres);

            // Ordenar cada grupo por fecha de inicio (del más cercano al más lejano)
            const campamentosOrdenados = this.ordenarPorFechaInicio(campamentos);
            const talleresVirtualesOrdenados = this.ordenarPorFechaInicio(talleresVirtuales);

            // Combinar: primero talleres virtuales, luego campamentos
            const talleresOrdenados = [...talleresVirtualesOrdenados, ...campamentosOrdenados];

            console.log('✅ Talleres obtenidos desde datos locales:', {
                fechaObtencion: tiendaNubeData.meta?.fechaObtencion,
                campamentos: campamentosOrdenados.length,
                talleresVirtuales: talleresVirtualesOrdenados.length,
                total: talleresOrdenados.length
            });

            return talleresOrdenados;
        } catch (error) {
            console.error('❌ Error al obtener talleres desde datos locales:', error);
            return [];
        }
    }

    // Método para obtener talleres con fallback al JSON local
    async getTalleresWithFallback() {
        try {
            // Intentar obtener talleres (ya maneja automáticamente el fallback a datos locales)
            const talleres = await this.getTalleres();

            // Si no hay productos o hay error, usar fallback al servicio local
            if (!talleres || talleres.length === 0) {
                console.log('No se pudieron obtener productos de TiendaNube, usando fallback al servicio local...');
                const { getTalleres } = await import('./productos.js');
                return getTalleres();
            }

            return talleres;
        } catch (error) {
            console.error('Error en getTalleresWithFallback, usando fallback al servicio local:', error);
            // Fallback al servicio local
            const { getTalleres } = await import('./productos.js');
            return getTalleres();
        }
    }

    // Método para refrescar cache manualmente
    async refreshCache() {
        console.log('🔄 Refrescando cache manualmente...');
        return await this.getTalleres(true);
    }

    // Método para obtener campamentos con cache
    async getCampamentos(forceRefresh = false) {
        const talleres = await this.getTalleres(forceRefresh);
        return talleres.filter(t => t.esCampamento || t.tipo === 'campamento');
    }

    // Método para obtener talleres virtuales con cache
    async getTalleresVirtuales(forceRefresh = false) {
        const talleres = await this.getTalleres(forceRefresh);
        return talleres.filter(t => !t.esCampamento && t.tipo !== 'campamento');
    }

    // ============================================
    // MÉTODOS PARA GESTIÓN DE CUPONES DE DESCUENTO
    // ============================================

    /**
     * Crear un cupón de descuento en TiendaNube
     * @param {Object} options - Opciones del cupón
     * @param {string} options.code - Código del cupón (único, solo alfanumérico)
     * @param {string} options.type - Tipo: 'percentage', 'absolute' o 'shipping'
     * @param {string|number} options.value - Valor del descuento (obligatorio si type es percentage o absolute)
     * @param {string} options.start_date - Fecha de inicio (YYYY-MM-DD)
     * @param {string} options.end_date - Fecha de fin (YYYY-MM-DD)
     * @param {number} [options.max_uses=1] - Usos máximos (default: 1 para uso único)
     * @param {number} [options.min_price=0] - Monto mínimo del carrito para aplicar
     * @param {boolean} [options.includes_shipping=false] - Si el descuento aplica al envío
     * @param {boolean} [options.combines_with_other_discounts=false] - Si se puede combinar con otras promos
     * @param {boolean} [options.first_consumer_purchase=false] - Solo primera compra del cliente
     * @param {Array<number>} [options.categories] - IDs de categorías permitidas
     * @param {Array<number>} [options.products] - IDs de productos permitidos
     * @returns {Promise<Object>} Cupón creado
     */
    async createCoupon(options) {
        try {
            const {
                code,
                type,
                value,
                start_date,
                end_date,
                max_uses = 1,
                min_price = 0,
                includes_shipping = false,
                combines_with_other_discounts = false,
                first_consumer_purchase = false,
                categories = null,
                products = null
            } = options;

            // Validaciones
            if (!code) {
                throw new Error('El código del cupón es requerido');
            }

            // Validar que el código sea solo alfanumérico
            if (!/^[A-Z0-9]+$/i.test(code)) {
                throw new Error('El código del cupón solo puede contener letras y números');
            }

            if (!type || !['percentage', 'absolute', 'shipping'].includes(type)) {
                throw new Error('El tipo de descuento debe ser "percentage", "absolute" o "shipping"');
            }

            if ((type === 'percentage' || type === 'absolute') && !value) {
                throw new Error('El valor del descuento es requerido para tipos percentage o absolute');
            }

            if (!start_date || !end_date) {
                throw new Error('Las fechas de inicio y fin son requeridas (formato YYYY-MM-DD)');
            }

            // Construir el payload según la API oficial de TiendaNube
            const payload = {
                code: code.toUpperCase().trim(),
                type: type,
                value: type === 'percentage' || type === 'absolute' ? String(value) : undefined,
                start_date: start_date,
                end_date: end_date,
                max_uses: max_uses,
                min_price: min_price,
                includes_shipping: includes_shipping,
                combines_with_other_discounts: combines_with_other_discounts,
                first_consumer_purchase: first_consumer_purchase,
                only_cheapest_shipping: false
            };

            // Agregar campos opcionales solo si tienen valor
            if (categories && categories.length > 0) {
                payload.categories = categories;
            } else {
                payload.categories = null;
            }

            if (products && products.length > 0) {
                payload.products = products;
            } else {
                payload.products = null;
            }

            console.log('🎫 Creando cupón en TiendaNube:', payload);

            // Usar el endpoint correcto: /coupons (no /discounts)
            const response = await axios.post(
                getTiendaNubeApiUrl('coupons'),
                payload,
                { headers: this.getHeaders() }
            );

            console.log('✅ Cupón creado exitosamente:', response.data);
            return response.data;
        } catch (error) {
            console.error('❌ Error al crear cupón:', error.response?.data || error.message);
            if (error.response?.data) {
                console.error('📋 Detalles del error:', JSON.stringify(error.response.data, null, 2));
            }
            throw error;
        }
    }

    /**
     * Crear un cupón de cumpleaños personalizado
     * @param {Object} options - Opciones del cupón de cumpleaños
     * @param {string} options.customerEmail - Email del cliente
     * @param {string} options.customerName - Nombre del cliente
     * @param {number} options.discount_percent - Porcentaje de descuento (ej: 15)
     * @param {number} options.daysValid - Días de validez desde hoy (default: 7)
     * @param {string} [options.customCode] - Código personalizado (si no se proporciona, se genera automáticamente)
     * @returns {Promise<Object>} Cupón creado con información adicional
     */
    async createBirthdayCoupon(options) {
        try {
            const {
                customerEmail,
                customerName,
                discount_percent = 15,
                daysValid = 7,
                customCode = null
            } = options;

            // Generar código único si no se proporciona
            let code = customCode || this.generateBirthdayCode(customerEmail);
            
            // Limpiar el código: solo letras y números, convertir a mayúsculas
            code = code.toUpperCase().replace(/[^A-Z0-9]/g, '');
            
            // Validar que el código no esté vacío después de limpiar
            if (!code || code.length === 0) {
                throw new Error('El código generado no es válido. Intenta con un código personalizado.');
            }

            // Calcular fechas en formato YYYY-MM-DD
            const startDate = new Date();
            const endDate = new Date();
            endDate.setDate(endDate.getDate() + daysValid);

            const start_date = startDate.toISOString().split('T')[0]; // YYYY-MM-DD
            const end_date = endDate.toISOString().split('T')[0]; // YYYY-MM-DD

            // Crear el cupón usando el formato correcto de la API
            const coupon = await this.createCoupon({
                code,
                type: 'percentage',
                value: String(discount_percent.toFixed(2)), // Formato: "15.00"
                start_date,
                end_date,
                max_uses: 1, // Uso único
                min_price: 0,
                includes_shipping: false,
                combines_with_other_discounts: false,
                first_consumer_purchase: false,
                categories: null,
                products: null
            });

            // Retornar información completa del cupón
            return {
                ...coupon,
                customerEmail,
                customerName,
                discountDescription: `${discount_percent}% de descuento`,
                expiryDate: endDate.toLocaleDateString('es-AR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                }),
                expiryDateISO: end_date
            };
        } catch (error) {
            console.error('❌ Error al crear cupón de cumpleaños:', error);
            throw error;
        }
    }

    /**
     * Generar un código único para cupón de cumpleaños
     * @param {string} email - Email del cliente para generar código único
     * @returns {string} Código generado (solo alfanumérico, sin guiones)
     */
    generateBirthdayCode(email) {
        // Usar hash del email + timestamp para generar código único
        const hash = this.simpleHash(email + Date.now());
        const randomSuffix = Math.random().toString(36).substring(2, 6).toUpperCase();
        
        // Generar código solo alfanumérico: CUMPLE + hash + random
        // Formato: CUMPLE + 8 caracteres alfanuméricos = 13 caracteres total
        const code = `CUMPLE${hash.substring(0, 4)}${randomSuffix}`.toUpperCase();
        
        // Asegurar que solo contenga letras y números
        return code.replace(/[^A-Z0-9]/g, '');
    }

    /**
     * Función auxiliar para generar hash simple
     * @param {string} str - String a hashear
     * @returns {string} Hash hexadecimal
     */
    simpleHash(str) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convertir a entero de 32 bits
        }
        return Math.abs(hash).toString(16).padStart(8, '0');
    }

    /**
     * Obtener información de un cupón por código
     * @param {string} code - Código del cupón
     * @returns {Promise<Object>} Información del cupón
     */
    async getCouponByCode(code) {
        try {
            const response = await axios.get(
                getTiendaNubeApiUrl('coupons'),
                {
                    headers: this.getHeaders(),
                    params: { code: code.toUpperCase().trim() }
                }
            );

            // La API puede retornar un array, tomar el primero
            const coupons = response.data;
            if (Array.isArray(coupons) && coupons.length > 0) {
                return coupons[0];
            }

            return null;
        } catch (error) {
            console.error(`❌ Error al obtener cupón ${code}:`, error);
            throw error;
        }
    }

    /**
     * Obtener todos los cupones
     * @returns {Promise<Array>} Lista de cupones
     */
    async getAllCoupons() {
        try {
            const response = await axios.get(
                getTiendaNubeApiUrl('coupons'),
                { headers: this.getHeaders() }
            );

            return response.data;
        } catch (error) {
            console.error('❌ Error al obtener cupones:', error);
            throw error;
        }
    }

    /**
     * Eliminar/desactivar un cupón
     * @param {number} couponId - ID del cupón
     * @returns {Promise<Object>} Resultado de la eliminación
     */
    async deleteCoupon(couponId) {
        try {
            const response = await axios.delete(
                getTiendaNubeApiUrl(`coupons/${couponId}`),
                { headers: this.getHeaders() }
            );

            console.log(`✅ Cupón ${couponId} eliminado exitosamente`);
            return response.data;
        } catch (error) {
            console.error(`❌ Error al eliminar cupón ${couponId}:`, error);
            throw error;
        }
    }
}

// Crear instancia singleton
const tiendaNubeService = new TiendaNubeService();

// Exportar instancia y también métodos útiles para debugging
export default tiendaNubeService;

// Exportar métodos de utilidad para la consola del navegador
export const clearCache = () => tiendaNubeService.clearCache();
export const getCacheInfo = () => tiendaNubeService.getCacheInfo();
export const refreshCache = () => tiendaNubeService.refreshCache();
